import React, { useState } from 'react';
import { Tooltip } from 'devextreme-react/tooltip';
import { RadioGroup } from 'devextreme-react';
import cn from 'classnames';
import { isSameOrBefore } from '../../../utils/helper';
import TextBox from '../../TextBox';
import SelectBox from '../../SelectBox';
import NumberBox from '../../NumberBox';
import DateBox from '../../DateBox';
import TextArea from '../../TextArea';
import CheckBoxField from '../../CheckBoxField';
import Label from '../../Label';
import Title from '../../Title';
import Footnote from '../../Footnote';
import Controls from '../../Controls';
import FieldError from '../../FieldError';
import { secTypes, secClasses } from '../../../constants/securityDetails';
import { errors, urls, reportingStandards, noPriceQuotationOptions } from '../../../constants';
import { isNumber, isCusip, isIsin } from '../../../utils/validations';
import styles from '../Application.module.scss';
import secStyles from './SecurityDetails.module.scss';

const maxDate = new Date();
const incompleteError = errors.INCOMPLETE_DATA_FIELD;
const sharesAuthorizedTitleId = 'sharesAuthorizedTitle';
const parValueTitleId = 'parValueTitle';
const publicFloatTitleId = 'publicFloatTitle';
const sharesAuthorizedTooltipText = 'Authorized Shares refers to the maximum number of shares the company is allowed to issue to shareholders under its articles of incorporation. Authorized shares are traditionally affiliated with U.S. domiciled companies as compared to non-U.S. domiciled companies.';
const parValueTooltipText = 'Par value refers to the per-share value assigned by the company under its articles of incorporation. Par values are traditionally affiliated with U.S. domiciled companies, whereas non-U.S. domiciled companies are generally not subject to par value requirements.';
const publicFloatTooltipText = 'For U.S. and Canadian companies, to assist with calculating public float, your Transfer Agent may be able to provide a shareholder report that identifies the number of shares that are ‘restricted’ vs. ‘unrestricted’.  Please note that although securities are eligible to be sold pursuant to a resale S-1 or Rule 144 holding period,  your Transfer Agent may require additional documentation such as an opinion letter to remove any pre-existing legend.';

const priceQuotationOptions = [
  {
    id: noPriceQuotationOptions.INITIAL_REVIEW,
    value: <Title title={() => <>Request OTC Markets Group to conduct an <a href={urls.INITIAL_REVIEW_GLOSSARY} target='_blank' rel='noopener noreferrer'>Initial Review</a> and file a Form 211 with FINRA.</>} type='h3' />,
  },
  {
    id: noPriceQuotationOptions.FORM_211,
    value: 'Another broker-dealer is filing a Form 211 with FINRA.'
  }
];

const secDetailObj = {
  isQXtraded: false,
  isQBtraded: false,
  type: '',
  securityClass: '',
  cusip: '',
  authorizedShares: '',
  isAuthorizedUnlimited: false,
  restrictedShares: '',
  unrestrictedShares: '',
  sharesOutstanding: '',
  parValue: '',
  noParValue: false,
  publicFloat: '',
  publicFloatPercentage: '',
  sharesAsOfDate: '',
  beneficialShareholders: '',
  ownerships: []
};

const AddSecurityDetail = ({ securityView, setSlidePane, handleAdd, handleUpdate, readOnly, tradedField, isTradedText, showIncompletes, reportingStandard, isDNS }) => {
  const [secDetail, setSecDetail] = useState(securityView || { ...secDetailObj, [tradedField]: true });
  const [showSharesAuthorizedToolTip, setShowSharesAuthorizedToolTip] = useState(false);
  const [showParValueToolTip, setShowParValueToolTip] = useState(false);
  const [showPublicFloatToolTip, setShowPublicFloatToolTip] = useState(false);
  const [authorizedSharesFocus, setAuthorizedSharesFocus] = useState(false);
  const [sharesAsOfDateFocus, setSharesAsOfDateFocus] = useState(false);
  const [sharesOutstandingFocus, setSharesOutstandingFocus] = useState(false);
  const [restrictedSharesFocus, setRestrictedSharesFocus] = useState(false);
  const [unrestrictedSharesFocus, setUnrestrictedSharesFocus] = useState(false);
  const [beneficialShareholdersFocus, setBeneficialShareholdersFocus] = useState(false);
  const [unrestrictedSharesAffiliatesFocus, setUnrestrictedSharesAffiliatesFocus] = useState(false);
  const [parValueFocus, setParValueFocus] = useState(false);
  const [priceQuotationFocus, setPriceQuotationFocus] = useState(false);
  const isAdd = !securityView;
  const hasSharesOutstandingValue = isNumber(secDetail.sharesOutstanding);
  const hasRestrictedSharesValue = isNumber(secDetail.restrictedShares);
  const hasUnrestrictedSharesValue = isNumber(secDetail.unrestrictedShares);
  const isSharesOutstandingValid = secDetail.isAuthorizedUnlimited || secDetail.sharesOutstanding <= secDetail.authorizedShares;
  const isRestrictedSharesValid = secDetail.restrictedShares <= secDetail.sharesOutstanding;
  const isUnrestrictedSharesValid = (secDetail.unrestrictedShares + secDetail.restrictedShares) === secDetail.sharesOutstanding;
  const isUnrestrictedSharesAffiliatesValid = secDetail.unrestrictedSharesAffiliates <= secDetail.unrestrictedShares;
  const setValidUnrestrictedShares = secDetail.unrestrictedShares ? isUnrestrictedSharesValid : true;
  const setValidUnresSharesAffilates = secDetail.unrestrictedSharesAffiliates ? isNumber(secDetail.unrestrictedSharesAffiliates) : true;
  const publicValid = hasSharesOutstandingValue && hasRestrictedSharesValue && hasUnrestrictedSharesValue && isSharesOutstandingValid && isRestrictedSharesValid && isUnrestrictedSharesValid;
  const publicFloat = secDetail.sharesOutstanding - secDetail.restrictedShares - secDetail.unrestrictedSharesAffiliates;
  const publicFloatPercentage = (publicFloat / secDetail.sharesOutstanding) * 100;
  const hasPricedQuotation = secDetail?.hasPricedQuotation;
  const noPriceQuotationOption = secDetail.noPriceQuotationOption;
  const isDateSameOrBefore = isSameOrBefore(secDetail.sharesAsOfDate);
  const isIntlReportStnd = reportingStandard === reportingStandards.INTERNATIONAL;
  const showParValueFieldError = (showIncompletes && !secDetail.noParValue) && !isNumber(secDetail && secDetail.parValue);
  const hasSingleFE = !!(secDetail && secDetail.metadata && secDetail.metadata.length === 1 && secDetail.foreignExchangeSymbol && secDetail.foreignExchangeName && secDetail.metadata[0].symbol.length === 5);
  const hasMultipleFE = !!(secDetail && secDetail.metadata && secDetail.metadata.length > 1 && secDetail.foreignExchangeSymbol && secDetail.foreignExchangeName);

  const onValueChanged = (value, field) => {
    setSecDetail(prevState => {
      return {
        ...prevState,
        [field]: value
      };
    });

    if (field === 'isAuthorizedUnlimited' && value === true) {
      setSecDetail(prevState => {
        return {
          ...prevState,
          authorizedShares: null
        };
      });
    }

    if (field === 'noParValue' && value === true) {
      setSecDetail(prevState => {
        return {
          ...prevState,
          parValue: null
        };
      });
    }
  };

  const customFormatter = {
    formatter: (value) => {
      // Format the number as percentage with 2 decimal places
      if (!publicValid || !isNumber(value)) return 'N/A';

      return `${parseFloat(value).toFixed(2)}%`;
    }
  };

  const handleUpdateDetail = () => {
    handleUpdate({ ...secDetail });
  };

  const handleSubmit = () => {
    handleAdd({ ...secDetail });
  };

  const renderSharesAuthorizedTitle = () => {
    return <a id={sharesAuthorizedTitleId} onMouseEnter={() => setShowSharesAuthorizedToolTip(true)} onMouseLeave={() => setShowSharesAuthorizedToolTip(false)}>Shares Authorized</a>;
  };

  const renderParValueTitle = () => {
    return <a id={parValueTitleId} onMouseEnter={() => setShowParValueToolTip(true)} onMouseLeave={() => setShowParValueToolTip(false)}>Par Value</a>;
  };

  const renderPublicFloatTitle = () => {
    return <a id={publicFloatTitleId} onMouseEnter={() => setShowPublicFloatToolTip(true)} onMouseLeave={() => setShowPublicFloatToolTip(false)}>Public Float</a>;
  };

  const singleFeLabel = <>
    Provide information for the underlying Security {secDetail && secDetail.foreignExchangeSymbol} on {secDetail && secDetail.foreignExchangeName}.
  </>;

  const mulipleFeLabel = <>
    These trading symbols are linked to {secDetail && secDetail.foreignExchangeSymbol} on the {secDetail && secDetail.foreignExchangeName}. Please provide the share information below for {secDetail && secDetail.foreignExchangeSymbol}.
  </>;

  const renderCustomItem = data => {
    const initialReview = data.id === noPriceQuotationOptions.INITIAL_REVIEW;
    const showAdditionalInfo = secDetail.noPriceQuotationOption === noPriceQuotationOptions.INITIAL_REVIEW;

    return <div data-testid={`security-detail-quotation-${data.id}`} className={styles.radioItem}>
      <Title title={data.value} type='h3' />
      {(initialReview && showAdditionalInfo) && <>
        <p>
          Has any broker-dealer filed a Form 211 with FINRA for any of the company's securities in the last two years? If yes, confirm when it was filed, the broker-dealer’s name, and provide FINRA's comments.
        </p>
        <TextArea
          showClearButton
          placeholder='Form 211 information'
          name='form211Detail'
          maxLength={3000}
          readOnly={readOnly}
          errorText='Incomplete Data. Please enter detail.'
          value={secDetail.form211Detail}
          testId='security-detail-form211'
          onValueChanged={e => onValueChanged(e.value, 'form211Detail')}
        />
        <p>
          If the security does not have a trading symbol, provide your top 5 preferred trading symbols. Note: U.S. symbols = 4 letters, non-U.S. symbols = 5 letters ending with ‘F' or for an ADR, a 'Y’.
        </p>
        <TextArea
          placeholder='Symbol preference'
          name='symbolPreference'
          maxLength={100}
          readOnly={readOnly}
          value={secDetail.symbolPreference}
          showClearButton
          testId='security-detail-symbol-preference'
          onValueChanged={e => onValueChanged(e.value, 'symbolPreference')} />
      </>}
    </div>;
  };

  return (
    <div>
      {hasSingleFE && <Label className='mbLg' title={singleFeLabel} />}
      {hasMultipleFE && <Label className='mbLg' title={mulipleFeLabel} />}
      <p className={styles.description}>
        Provide information for the security.
      </p>
      {!isDNS && <CheckBoxField
        text={isTradedText}
        readOnly={readOnly}
        value={secDetail[tradedField]}
        testId='security-detail-is-traded'
        onValueChanged={e => onValueChanged(e.value, tradedField)} />}
      {!secDetail.isExistingSecurity && <div className={cn(styles.innerGrid, 'mtMed')}>
        <div className={styles.col3}>
          <SelectBox
            label='Security Type'
            name='secType'
            placeholder='Select Type'
            isRequired={showIncompletes}
            items={secTypes}
            tabIndex={1}
            readOnly={readOnly}
            value={secDetail.type}
            testId='security-detail-type'
            onValueChanged={e => onValueChanged(e.value, 'type')} />
        </div>
        <div className={styles.col3}>
          <SelectBox
            label='Security Class'
            name='secClass'
            placeholder='Select Class'
            isRequired={showIncompletes}
            valueExpr='id'
            displayExpr='name'
            tabIndex={2}
            items={secClasses}
            readOnly={readOnly}
            value={secDetail.securityClass}
            testId='security-detail-class'
            onValueChanged={e => onValueChanged(e.value, 'securityClass')} />
        </div>
        <div className={styles.col3}>
          <TextBox
            label='CUSIP Number'
            placeholder='XXXXXXXXX'
            name='cusip'
            tabIndex={3}
            maxLength={9}
            isRequired={showIncompletes && !isIntlReportStnd}
            isValid={secDetail.cusip ? isCusip(secDetail.cusip) : true}
            errorText={secDetail.cusip ? errors.CUSIP : null}
            readOnly={readOnly}
            value={secDetail.cusip}
            showClearButton
            testId='security-detail-cusip'
            onValueChanged={e => onValueChanged(e.value, 'cusip')} />
        </div>
        {isIntlReportStnd && <div className={styles.col3}>
          <TextBox
            label='ISIN'
            placeholder='XX-XXXXXXXXX-X'
            name='isin'
            tabIndex={5}
            maxLength={14}
            isValid={secDetail.isin ? isIsin(secDetail.isin) : true}
            errorText={secDetail.isin ? errors.ISIN : null}
            readOnly={readOnly}
            value={secDetail.isin}
            showClearButton
            testId='security-detail-isin'
            onValueChanged={e => onValueChanged(e.value, 'isin')} />
        </div>}
      </div>}
      <Title className='mtXL mbXL' title='Share Information' type='h2' />
      <div className={styles.innerGrid}>
        <div className={styles.col4}>
          <Title className={secStyles.title} title='The data in this section is as of:' type='h3' />
          {(!isDateSameOrBefore || (showIncompletes && !secDetail.sharesAsOfDate)) && <FieldError error={!isDateSameOrBefore ? errors.FUTURE_DATE : incompleteError} isFocus={sharesAsOfDateFocus} />}
        </div>
        <div className={styles.col2}>
          <DateBox
            className={'maxHeight'}
            showClearButton
            placeholder='MM/DD/YYYY'
            tabIndex={4}
            readOnly={readOnly}
            value={secDetail.sharesAsOfDate}
            type='date'
            hideErrorIcon
            onFocusIn={() => setSharesAsOfDateFocus(true)}
            onFocusOut={() => setSharesAsOfDateFocus(false)}
            isRequired={showIncompletes}
            max={maxDate}
            testId='security-detail-date'
            onValueChanged={e => onValueChanged(e.value, 'sharesAsOfDate')} />
        </div>
        <div className={styles.col4}>
          <div className={secStyles.wideTitle}>
            <Title className={secStyles.title} title={isDNS ? renderSharesAuthorizedTitle() : 'Shares Authorized'} type='h3' />
            <Tooltip
              maxWidth={600}
              target={`#${sharesAuthorizedTitleId}`}
              visible={showSharesAuthorizedToolTip}
              position='bottom'
              hideOnOutsideClick={false}>
              <div>
                {sharesAuthorizedTooltipText}
              </div>
            </Tooltip>
          </div>
          {(showIncompletes && !secDetail.isAuthorizedUnlimited) && !isNumber(secDetail && secDetail.authorizedShares) && <FieldError error={incompleteError} isFocus={authorizedSharesFocus} />}
        </div>
        <div className={styles.col2}>
          <NumberBox
            className={'maxHeight'}
            placeholder={secDetail.isAuthorizedUnlimited ? 'N/A' : 'XXX,XXX'}
            name='authorizedShares'
            tabIndex={5}
            format='#,##0'
            disabled={secDetail.isAuthorizedUnlimited}
            hideErrorIcon
            isRequired={showIncompletes && !secDetail.isAuthorizedUnlimited}
            readOnly={readOnly}
            value={!secDetail.isAuthorizedUnlimited ? secDetail.authorizedShares : undefined}
            showClearButton
            onFocusIn={() => setAuthorizedSharesFocus(true)}
            onFocusOut={() => setAuthorizedSharesFocus(false)}
            testId='security-detail-authorized-shares'
            onValueChanged={e => onValueChanged(e.value, 'authorizedShares')} />
        </div>
        <div className={styles.col4}>
          <div className={secStyles.subTitle}>Unlimited Number of Shares Authorized</div>
        </div>
        <CheckBoxField
          className={styles.col2}
          size='small'
          readOnly={readOnly}
          value={secDetail.isAuthorizedUnlimited}
          testId='security-detail-unlimited-shares'
          onValueChanged={e => onValueChanged(e.value, 'isAuthorizedUnlimited')} />
        <div className={styles.col4}>
          <Title title='Total Shares Outstanding' type='h3' className={secStyles.title} />
          {!isDNS && (showIncompletes && !secDetail.sharesOutstanding) && <FieldError error={incompleteError} isFocus={sharesOutstandingFocus} />}
          {(secDetail.sharesOutstanding && !isSharesOutstandingValid) ? <FieldError error='Shares Outstanding cannot be greater than Number of Shares Authorized.' isFocus={sharesOutstandingFocus} /> : ''}
        </div>
        <div className={styles.col2}>
          <NumberBox
            className='maxHeight'
            placeholder='XXX,XXX'
            format='#,##0'
            tabIndex={6}
            hideErrorIcon
            isValid={secDetail.sharesOutstanding ? isSharesOutstandingValid : true}
            name='sharesOutstanding'
            readOnly={readOnly}
            value={secDetail.sharesOutstanding}
            showClearButton
            onFocusIn={() => setSharesOutstandingFocus(true)}
            onFocusOut={() => setSharesOutstandingFocus(false)}
            testId='security-detail-shares-outstanding'
            onValueChanged={e => onValueChanged(e.value, 'sharesOutstanding')}
            {...(isDNS === false ? { isRequired: showIncompletes } : {})} />
        </div>
        {!isDNS && <>
          <div className={styles.col4}>
            <Title className={secStyles.title} title='Restricted Shares' type='h3' />
            {(showIncompletes && !secDetail.restrictedShares) && <FieldError error={incompleteError} isFocus={restrictedSharesFocus} />}
            {(secDetail.restrictedShares && !isRestrictedSharesValid) ? <FieldError error='Number of Restricted Shares cannot be greater than the Number of Shares Outstanding.' isFocus={restrictedSharesFocus} /> : ''}
          </div>

          <div className={styles.col2}>
            <NumberBox
              className='maxHeight'
              placeholder='XXX,XXX'
              format='#,##0'
              tabIndex={7}
              hideErrorIcon
              isRequired={showIncompletes}
              isValid={secDetail.restrictedShares ? isRestrictedSharesValid : true}
              name='restrictedShares'
              readOnly={readOnly}
              value={secDetail.restrictedShares}
              showClearButton
              onFocusIn={() => setRestrictedSharesFocus(true)}
              onFocusOut={() => setRestrictedSharesFocus(false)}
              testId='security-detail-restricted-shares'
              onValueChanged={e => onValueChanged(e.value, 'restrictedShares')} />
          </div>
        </>}
        {!isDNS && <>
          <div className={styles.col4}>
            <Title className={secStyles.title} title='Unrestricted Shares' type='h3' />
            {(showIncompletes && !secDetail.unrestrictedShares) && <FieldError error={incompleteError} isFocus={unrestrictedSharesFocus} />}
            {(secDetail.unrestrictedShares && !isUnrestrictedSharesValid) ? <FieldError error='Restricted Shares plus Unrestricted Shares should equal Total Shares Outstanding.' isFocus={unrestrictedSharesFocus} /> : ''}
          </div>
          <div className={styles.col2}>
            <NumberBox
              className='maxHeight'
              placeholder='XXX,XXX'
              format='#,##0'
              tabIndex={8}
              hideErrorIcon
              isRequired={showIncompletes}
              isValid={setValidUnrestrictedShares}
              name='unrestrictedShares'
              readOnly={readOnly}
              value={secDetail.unrestrictedShares}
              showClearButton
              onFocusIn={() => setUnrestrictedSharesFocus(true)}
              onFocusOut={() => setUnrestrictedSharesFocus(false)}
              testId='security-detail-unrestricted-shares'
              onValueChanged={e => onValueChanged(e.value, 'unrestrictedShares')} />
          </div>
        </>}
        {!isDNS && <>
          <div className={styles.col4}>
            <Title className={secStyles.wideTitle} title='Unrestricted Shares Held by Officers, Directors, 10% Holders & Affiliates' type='h3' />
            {(showIncompletes && !isNumber(secDetail.unrestrictedSharesAffiliates && secDetail.unrestrictedSharesAffiliates)) && <FieldError className={secStyles.alignTop} error={incompleteError} isFocus={unrestrictedSharesAffiliatesFocus} />}
            {secDetail.unrestrictedSharesAffiliates && !isUnrestrictedSharesAffiliatesValid ? <FieldError className={secStyles.alignTop} error='Unrestricted Shares should be greater than Unrestricted Shares Held by Officers, Directors, 10% Holders & Affiliates.' isFocus={unrestrictedSharesAffiliatesFocus} /> : ''}
          </div>
          <div className={styles.col2}>
            <NumberBox
              className='maxHeight'
              placeholder='XXX,XXX'
              format='#,##0'
              tabIndex={9}
              isRequired={showIncompletes}
              isValid={setValidUnresSharesAffilates}
              name='unrestrictedShares'
              hideErrorIcon
              readOnly={readOnly}
              value={secDetail.unrestrictedSharesAffiliates}
              showClearButton
              onFocusIn={() => setUnrestrictedSharesAffiliatesFocus(true)}
              onFocusOut={() => setUnrestrictedSharesAffiliatesFocus(false)}
              testId='security-detail-unrestricted-officers'
              onValueChanged={e => onValueChanged(e.value, 'unrestrictedSharesAffiliates')} />
          </div>
        </>}
        {!isDNS && <>
          <div className={styles.col4}>
            <div className={secStyles.wideTitle}>
              <Title title='Number of Beneficial Shareholders of at least 100 shares' type='h3' noMargin />
              <Footnote text={() => <>Beneficial Shareholder means any person who, directly or indirectly has or shares voting power of such security or investment power, which includes the power to dispose, or to direct the disposition of, such security.<br /><a href={urls.GLOSSARY_BENEFICIAL_SHAREHOLDER} target='_blank' rel='noopener noreferrer'>See Beneficial Shareholders definition</a></>} />
            </div>
            {(showIncompletes && !secDetail.beneficialShareholders) && <FieldError className={secStyles.alignTop} error={incompleteError} isFocus={beneficialShareholdersFocus} />}
          </div>
          <NumberBox
            className={cn(styles.col2, 'maxHeight')}
            placeholder='XXX'
            format='#,##0'
            name='beneficialShareholders'
            readOnly={readOnly}
            value={secDetail.beneficialShareholders}
            showClearButton
            tabIndex={10}
            hideErrorIcon
            isRequired={showIncompletes}
            onFocusIn={() => setBeneficialShareholdersFocus(true)}
            onFocusOut={() => setBeneficialShareholdersFocus(false)}
            testId='security-detail-beneficial-shareholders'
            onValueChanged={e => onValueChanged(e.value, 'beneficialShareholders')} />
        </>}
        <div className={styles.col4}>
          <div className={secStyles.wideTitle}>
            <Title title={isDNS ? renderParValueTitle() : 'Par Value'} type='h3' />
            <Tooltip
              maxWidth={600}
              target={`#${parValueTitleId}`}
              visible={showParValueToolTip}
              position='bottom'
              hideOnOutsideClick={false}>
              <div>
                {parValueTooltipText}
              </div>
            </Tooltip>
          </div>
          {showParValueFieldError && <FieldError error={incompleteError} isFocus={parValueFocus} />}
        </div>
        <div className={styles.col2}>
          <NumberBox
            className='maxHeight'
            placeholder={secDetail.noParValue ? 'N/A' : '$XXX'}
            name='parValue'
            format='#0.#####'
            disabled={secDetail.noParValue}
            hideErrorIcon
            tabIndex={11}
            readOnly={readOnly}
            value={!secDetail.noParValue ? secDetail.parValue : undefined}
            isRequired={showIncompletes && !secDetail.noParValue}
            onFocusIn={() => setParValueFocus(true)}
            onFocusOut={() => setParValueFocus(false)}
            showClearButton
            testId='security-detail-par-value'
            onValueChanged={e => onValueChanged(e.value, 'parValue')} />
        </div>
        <div className={styles.col4}>
          <div className={secStyles.subTitle}>No Par Value</div>
        </div>
        <CheckBoxField
          className={styles.col2}
          size='small'
          readOnly={readOnly}
          value={secDetail.noParValue}
          testId='security-detail-no-par-value'
          onValueChanged={e => onValueChanged(e.value, 'noParValue')} />
      </div>

      {!isDNS && <>
        <Title className='mtXL mbXL' title='Public Float Calculated from Share Information' type='h2' />
        <div className={styles.innerGrid}>
          <div className={styles.col4}>
            <Title title={renderPublicFloatTitle()} type='h3' noMargin />
            <Footnote text={() => <>Total Shares Outstanding less any Restricted Shares and any Unrestricted Shares Held by Officers, Directors, 10% Holders & Affiliates<br /><a href={urls.GLOSSARY_PUBLIC_FLOAT} target='_blank' rel='noopener noreferrer'>See Public Float definition</a></>} />
            <Tooltip
              maxWidth={600}
              target={`#${publicFloatTitleId}`}
              visible={showPublicFloatToolTip}
              position='bottom'
              hideOnOutsideClick={false}>
              <div>
                {publicFloatTooltipText}
              </div>
            </Tooltip>
          </div>
          <div className={styles.col2}>
            <NumberBox
              className='maxHeight'
              placeholder='N/A'
              format='#,##0'
              name='publicFloat'
              disabled
              readOnly={readOnly}
              value={publicFloat}
              onValueChanged={e => onValueChanged(e.value, 'publicFloat')}
            />
          </div>
          <div className={styles.col4}>
            <Title title='Public Float %' type='h3' noMargin />
            <Footnote text={() => <>Public Float as a percentage of Total Shares Outstanding<br /><a href={urls.GLOSSARY_PUBLIC_FLOAT} target='_blank' rel='noopener noreferrer'>See Public Float definition</a></>} />
          </div>
          <div className={styles.col2}>
            <NumberBox
              className='maxHeight'
              placeholder='N/A'
              name='publicFloatPercentage'
              disabled
              readOnly={readOnly}
              format={customFormatter}
              value={publicFloatPercentage}
              onValueChanged={e => onValueChanged(e.value, 'publicFloatPercentage')}
            />
          </div>
        </div>
      </>}

      {(!isDNS && !hasPricedQuotation) && <>
        <Title className='mtXL mbXL' title='Priced Quotations Required' type='h2' />
        <Label className='mbLg'>
          <Title
            type='h3'
            title={() => <>
              It appears that no broker-dealer is publishing priced quotations for your security and it is not <a href={urls.PQE_GLOSSARY} target='_blank' rel='noopener noreferrer'>Proprietary Quote Eligible</a>. Proprietary Quotes are required for OTCQX and OTCQB traded securities. Would you like OTC Markets Group to submit a Form 211 to FINRA to allow brokers to quote your stock or are you working with another broker-dealer to do so?
            </>}
          />
        </Label>
        <div
          onMouseEnter={(showIncompletes && !noPriceQuotationOption) ? () => setPriceQuotationFocus(true) : undefined}
          onMouseLeave={(showIncompletes && !noPriceQuotationOption) ? () => setPriceQuotationFocus(false) : undefined}>
          <div className={cn({
            [styles.incomplete]: (showIncompletes && !noPriceQuotationOption)
          })} style={{ paddingBottom: '8px' }}>
       {(showIncompletes && !noPriceQuotationOption) && <FieldError error='One Priced Quotation must be selected.' isFocus={priceQuotationFocus} />}
          </div>
          <RadioGroup
            id='radio-group-with-selection'
            valueExpr='id'
            items={priceQuotationOptions}
            itemRender={renderCustomItem}
            value={noPriceQuotationOption}
            readOnly={readOnly}
            onValueChanged={e => onValueChanged(e.value, 'noPriceQuotationOption')} />
        </div>
      </>}
      {!readOnly && <div className={cn('mtXXL', styles.controls)}>
        <Controls
          testId='security-detail-form-controls'
          cancelText='Cancel'
          submitText='Save'
          onCancelClick={() => setSlidePane(false)}
          onSubmitClick={isAdd ? handleSubmit : handleUpdateDetail} />
      </div>}
    </div>
  );
};

export default AddSecurityDetail;
